import {
  useRef,
  useState,
  useEffect,
  KeyboardEvent,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, Theme } from "@mui/material";
import TextField from "./TextField";
import useIsMobile from "../hooks/useIsMobile";

interface ConfirmationCodeInputProps {
  value: string;
  onChange: (code: string) => void;
  length?: number;
  error?: boolean;
  disabled?: boolean;
}

export interface ConfirmationCodeInputRef {
  focusLastInput: () => void;
}

export default forwardRef<ConfirmationCodeInputRef, ConfirmationCodeInputProps>(
  ({ value, onChange, length = 6, error = false, disabled = false }, ref) => {
    const [digits, setDigits] = useState<string[]>(Array(length).fill(""));
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const hasRedirectedToFirst = useRef(false);

    // Initialize from value prop only once
    useEffect(() => {
      const newDigits = Array(length).fill("");
      const valueDigits = value.split("");
      valueDigits.forEach((digit, index) => {
        if (index < length) {
          newDigits[index] = digit;
        }
      });
      setDigits(newDigits);
    }, []);

    const handleChange = (index: number, newValue: string) => {
      const sanitized = newValue.replace(/\D/g, "");

      // Handle deletion
      if (sanitized.length === 0) {
        const newDigits = [...digits];
        newDigits[index] = "";
        setDigits(newDigits);
        onChange(newDigits.join(""));
        return;
      }

      // Take the last character typed (handles replacement)
      const newDigit = sanitized[sanitized.length - 1];
      const newDigits = [...digits];
      newDigits[index] = newDigit;
      setDigits(newDigits);
      onChange(newDigits.join(""));

      // Auto-focus next input
      if (index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    };

    const handleKeyDown = (index: number, e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Backspace" && !digits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else if (e.key === "ArrowLeft" && index > 0) {
        e.preventDefault();
        const prevInput = inputRefs.current[index - 1];
        prevInput?.focus();
        prevInput?.select();
      } else if (e.key === "ArrowRight" && index < length - 1) {
        e.preventDefault();
        const nextInput = inputRefs.current[index + 1];
        nextInput?.focus();
        nextInput?.select();
      }
    };

    const handlePaste = (
      index: number,
      e: React.ClipboardEvent<HTMLDivElement>,
    ) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData("text");
      const sanitized = pastedData.replace(/\D/g, "");

      if (sanitized.length > 0) {
        const newDigits = [...digits];
        const pastedDigits = sanitized.split("").slice(0, length - index);
        pastedDigits.forEach((digit, i) => {
          if (index + i < length) {
            newDigits[index + i] = digit;
          }
        });
        setDigits(newDigits);
        onChange(newDigits.join(""));

        const nextEmptyIndex = newDigits.findIndex(
          (d, i) => i > index && d === "",
        );
        if (nextEmptyIndex !== -1) {
          inputRefs.current[nextEmptyIndex]?.focus();
        } else {
          inputRefs.current[length - 1]?.focus();
        }
      }
    };

    const handleFocus = (index: number) => {
      // On a fresh page, redirect focus to the first input (only once)
      if (!hasRedirectedToFirst.current && digits.every((d) => d === "")) {
        hasRedirectedToFirst.current = true;
        if (index !== 0) {
          inputRefs.current[0]?.focus();
          return;
        }
      }
      inputRefs.current[index]?.select();
    };

    // Expose focusLastInput method via ref
    useImperativeHandle(ref, () => ({
      focusLastInput: () => {
        // Find the last filled input or default to last input
        let lastFilledIndex = -1;
        for (let i = digits.length - 1; i >= 0; i--) {
          if (digits[i] !== "") {
            lastFilledIndex = i;
            break;
          }
        }
        const indexToFocus =
          lastFilledIndex >= 0 ? lastFilledIndex : length - 1;
        inputRefs.current[indexToFocus]?.focus();
        inputRefs.current[indexToFocus]?.select();
      },
    }));

    const isMobile = useIsMobile();

    return (
      <Box
        sx={{
          display: "flex",
          gap: isMobile ? "8px" : "12px",
          justifyContent: "start",
        }}
      >
        {digits.map((digit, index) => (
          <TextField
            key={index}
            inputRef={(el: HTMLInputElement | null) =>
              (inputRefs.current[index] = el)
            }
            value={digit}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(index, e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) =>
              handleKeyDown(index, e)
            }
            onPaste={(e: React.ClipboardEvent<HTMLDivElement>) =>
              handlePaste(index, e)
            }
            onFocus={() => handleFocus(index)}
            disabled={disabled}
            inputProps={{
              maxLength: 1,
              inputMode: "numeric",
              autoComplete: "one-time-code",
              style: {
                textAlign: "center",
                fontSize: isMobile ? "24px" : "28px",
                fontWeight: "500",
                margin: 0,
              },
            }}
            sx={(theme: Theme) => ({
              width: "60px",
              "& .MuiOutlinedInput-root": {
                minHeight: "unset",
                height: isMobile ? "auto" : "76px",
                padding: 0,
                caretColor: "#198282",
                borderRadius: "12px",
                "& fieldset": {
                  borderColor: error
                    ? theme.palette.secondary.main
                    : theme.palette.border,
                  borderWidth: "2px",
                },
                "&:hover fieldset": {
                  borderColor: error
                    ? theme.palette.secondary.main
                    : theme.palette.border,
                  borderWidth: "2px",
                },
                "&.Mui-focused fieldset": {
                  borderColor: error
                    ? theme.palette.secondary.main
                    : theme.palette.border,
                  borderWidth: "2px",
                  boxShadow: theme.elevation[1],
                },
                "&.Mui-disabled": {
                  backgroundColor: "#F6F6F6",
                },
              },
              "& .MuiOutlinedInput-input": {
                padding: "16px",
                marginTop: 0,
              },
            })}
          />
        ))}
      </Box>
    );
  },
);
